import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
  Text,
  Flex,
  Icon,
  HStack,
  VStack,
  Box
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: 'Yes' | 'No') => void;
  title: string;
  messages: string[];
  type?: 'warning' | 'info' | 'confirmation';
  button1?: string;
  button2?: string | null;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  messages,
  type = 'warning',
  button1 = 'OK',
  button2 = null
}) => {
  const getIconAndColor = () => {
    switch (type) {
      case 'warning':
        return {
          icon: FaExclamationTriangle,
          color: 'orange.300',
          bgColor: 'orange.900',
          borderColor: 'orange.700'
        };
      case 'info':
        return {
          icon: FaInfoCircle,
          color: 'blue.300',
          bgColor: 'blue.900',
          borderColor: 'blue.700'
        };
      case 'confirmation':
        return {
          icon: FaQuestionCircle,
          color: 'purple.300',
          bgColor: 'purple.900',
          borderColor: 'purple.700'
        };
      default:
        return {
          icon: FaExclamationTriangle,
          color: 'orange.300',
          bgColor: 'orange.900',
          borderColor: 'orange.700'
        };
    }
  };

  const { icon: IconComponent, color, bgColor, borderColor } = getIconAndColor();

  const handleButton1Click = () => {
    onConfirm('Yes');
    onClose();
  };

  const handleButton2Click = () => {
    onConfirm('No');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent maxW="lg" mx={4} borderRadius="xl" boxShadow="2xl" bg="gray.800" color="gray.100">
        <ModalHeader pb={4} pt={6}>
          <HStack spacing={4} align="center">
            <Box
              w={12}
              h={12}
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              bg={bgColor}
              border="2px solid"
              borderColor={borderColor}
              boxShadow="sm"
            >
              <Icon as={IconComponent} color={color} boxSize={6} />
            </Box>
            <Text fontSize="xl" fontWeight="bold" color="white" lineHeight="1.2">
              {title}
            </Text>
          </HStack>
        </ModalHeader>
        
        <ModalBody pt={0} pb={6} px={6}>
          {messages.map((message, index) => (
            <Text 
              key={index}
            color="gray.200" 
            lineHeight="1.3" 
            fontSize="md"
            mb={4}
            >
              {message}
            </Text>
          ))}
        </ModalBody>

        <ModalFooter pt={0} pb={6} px={6}>
          <HStack spacing={4} w="full" justify="flex-end">
            {button2 && (
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={handleButton2Click}
                minW="140px"
                size="md"
                fontWeight="semibold"
                borderWidth="2px"
                _hover={{
                  bg: "gray.700",
                  borderColor: "gray.500"
                }}
              >
                {button2}
              </Button>
            )}
            <Button
              colorScheme={type === 'warning' ? 'orange' : type === 'info' ? 'blue' : 'purple'}
              onClick={handleButton1Click}
              minW="160px"
              size="md"
              fontWeight="semibold"
              _hover={{
                transform: "translateY(-1px)",
                boxShadow: "lg"
              }}
              transition="all 0.2s"
            >
              {button1}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmDialog;
